import type { AppItem } from "@/types/app";

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

export type MyProfile = {
  id?: string;
  displayName?: string | null;
  givenName?: string | null;
  surname?: string | null;
  mail?: string | null;
  jobTitle?: string | null;
  mobilePhone?: string | null;
  userPrincipalName?: string | null;
  otherMails?: string[] | null;
};

export type UpdatableProfileFields = Partial<
  Pick<
    MyProfile,
    "displayName" | "givenName" | "surname" | "mail" | "jobTitle" | "mobilePhone"
  >
>;

export type UpdateProfileResult = {
  applied: (keyof UpdatableProfileFields)[];
  skipped: Array<{ field: keyof UpdatableProfileFields; reason: string }>;
};

export class GraphError extends Error {
  status: number;
  code: string | undefined;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function parseError(res: Response): Promise<GraphError> {
  let code: string | undefined;
  let message = `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string };
    };
    if (body?.error) {
      code = body.error.code;
      message = body.error.message ?? message;
    }
  } catch {
    /* noop */
  }
  return new GraphError(message, res.status, code);
}

function authHeader(accessToken: string): HeadersInit {
  return {
    authorization: `Bearer ${accessToken}`,
    accept: "application/json",
  };
}

// ---------------------------------------------------------------------------
// /me — profile read & update
// ---------------------------------------------------------------------------

export async function fetchMyProfile(
  accessToken: string,
  signal?: AbortSignal,
): Promise<MyProfile> {
  const select =
    "id,displayName,givenName,surname,mail,jobTitle,mobilePhone,userPrincipalName,otherMails";
  const res = await fetch(`${GRAPH_BASE}/me?$select=${select}`, {
    headers: authHeader(accessToken),
    signal,
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as MyProfile;
}

/**
 * Fields Entra frequently refuses to update for self-service users.
 * When a PATCH fails with 403 we retry without these so the rest of
 * the patch can still succeed.
 */
const FIELDS_OFTEN_RESTRICTED: (keyof UpdatableProfileFields)[] = ["mail"];

export async function updateMyProfile(
  accessToken: string,
  patch: UpdatableProfileFields,
  signal?: AbortSignal,
): Promise<UpdateProfileResult> {
  const fields = Object.keys(patch) as (keyof UpdatableProfileFields)[];
  if (fields.length === 0) return { applied: [], skipped: [] };

  const send = async (body: UpdatableProfileFields) =>
    fetch(`${GRAPH_BASE}/me`, {
      method: "PATCH",
      headers: {
        ...authHeader(accessToken),
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
      signal,
    });

  const res = await send(patch);
  if (res.ok) return { applied: fields, skipped: [] };

  const err = await parseError(res);
  const triggers = FIELDS_OFTEN_RESTRICTED.filter((f) => f in patch);
  const shouldRetry =
    triggers.length > 0 &&
    (err.code === "Authorization_RequestDenied" || err.status === 403);
  if (!shouldRetry) throw err;

  const retained: UpdatableProfileFields = { ...patch };
  for (const t of triggers) delete retained[t];

  if (Object.keys(retained).length === 0) {
    return {
      applied: [],
      skipped: triggers.map((field) => ({
        field,
        reason:
          err.message ||
          "Your administrator has restricted self-service edits for this field.",
      })),
    };
  }

  const retry = await send(retained);
  if (!retry.ok) throw await parseError(retry);

  return {
    applied: Object.keys(retained) as (keyof UpdatableProfileFields)[],
    skipped: triggers.map((field) => ({
      field,
      reason:
        err.message ||
        "Your administrator has restricted self-service edits for this field.",
    })),
  };
}

// ---------------------------------------------------------------------------
// /me/changePassword — self-service password change
// ---------------------------------------------------------------------------

/**
 * Change the signed-in user's password via Microsoft Graph.
 *
 * Endpoint: POST /me/changePassword
 *   https://learn.microsoft.com/graph/api/user-changepassword
 *
 * Required delegated permission: Directory.AccessAsUser.All (admin
 * consent). Supported for Workforce Entra AND External ID (CIAM)
 * tenants when the tenant admin has granted consent.
 *
 * Common error codes to surface to the user:
 *   * InvalidAuthenticationToken  (401) – current password wrong OR
 *     silent token refresh expired.
 *   * Authorization_RequestDenied (403) – admin has not consented to
 *     Directory.AccessAsUser.All for the SPA app registration.
 *   * Request_BadRequest          (400) – new password fails the
 *     tenant password-complexity policy.
 */
export async function changeMyPassword(
  accessToken: string,
  currentPassword: string,
  newPassword: string,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${GRAPH_BASE}/me/changePassword`, {
    method: "POST",
    headers: {
      ...authHeader(accessToken),
      "content-type": "application/json",
    },
    body: JSON.stringify({ currentPassword, newPassword }),
    signal,
  });
  if (res.status === 204) return;
  throw await parseError(res);
}

// ---------------------------------------------------------------------------
// /me/appRoleAssignments + /servicePrincipals — MyApps discovery
// ---------------------------------------------------------------------------

type AppRoleAssignment = {
  id: string;
  principalId: string;
  resourceId: string;
  resourceDisplayName?: string | null;
  appRoleId: string;
};

type ServicePrincipal = {
  id: string;
  appId: string;
  displayName?: string | null;
  accountEnabled?: boolean;
  homepage?: string | null;
  loginUrl?: string | null;
  notes?: string | null;
  tags?: string[] | null;
  servicePrincipalType?: string | null;
  appRoleAssignmentRequired?: boolean;
  info?: {
    logoUrl?: string | null;
    termsOfServiceUrl?: string | null;
    supportUrl?: string | null;
    privacyStatementUrl?: string | null;
  } | null;
};

/* ------------------------------------------------------------------ */
/*  Discovery diagnostics                                              */
/* ------------------------------------------------------------------ */

/**
 * A single HTTP call made during app discovery. Recorded so the
 * troubleshoot panel can explain *exactly* which call failed and why
 * – especially useful in CIAM where several endpoints will return
 * 403 Authorization_RequestDenied by design.
 */
export type DiscoveryCall = {
  label: string;
  url: string;
  method: "GET" | "POST";
  status: number | null;
  ok: boolean;
  durationMs: number;
  errorCode?: string;
  errorMessage?: string;
  itemCount?: number;
  skipped?: boolean;
  skipReason?: string;
};

export type DiscoveryDiagnostics = {
  /** Tenant type we inferred at the time the discovery ran. */
  tenantKind: "workforce" | "ciam" | "unknown";
  /** The scopes the caller requested when acquiring the Graph token. */
  tokenScopesRequested: string[];
  /** Whether a token was successfully acquired. */
  tokenAcquired: boolean;
  tokenError?: string;
  calls: DiscoveryCall[];
  /** Apps produced by the Graph pipeline (before entitlement filtering). */
  graphAppsCount: number;
  /** Apps provided by runtime config (VITE_APPS_JSON). */
  configAppsCount: number;
  /** Overall summary, rendered prominently in the debug panel. */
  summary: string;
  /** When the diagnostics snapshot was taken. */
  capturedAt: string;
};

async function timedFetch(
  url: string,
  accessToken: string,
  label: string,
  signal?: AbortSignal,
): Promise<{ call: DiscoveryCall; json?: unknown }> {
  const start = performance.now();
  const call: DiscoveryCall = {
    label,
    url,
    method: "GET",
    status: null,
    ok: false,
    durationMs: 0,
  };
  try {
    const res = await fetch(url, { headers: authHeader(accessToken), signal });
    call.status = res.status;
    call.durationMs = Math.round(performance.now() - start);
    if (res.ok) {
      const json = (await res.json()) as unknown;
      call.ok = true;
      return { call, json };
    }
    try {
      const body = (await res.json()) as {
        error?: { code?: string; message?: string };
      };
      call.errorCode = body.error?.code;
      call.errorMessage = body.error?.message;
    } catch {
      /* ignore */
    }
    return { call };
  } catch (err) {
    call.durationMs = Math.round(performance.now() - start);
    call.errorMessage = err instanceof Error ? err.message : String(err);
    return { call };
  }
}

function deriveCategory(sp: ServicePrincipal | null): string {
  if (!sp?.tags) return "Apps";
  const custom = sp.tags.find((t) => t.toLowerCase().startsWith("category:"));
  if (custom) return custom.slice("category:".length).trim() || "Apps";
  if (sp.servicePrincipalType === "ManagedIdentity") return "System";
  return "Apps";
}

function myAppsLaunchUrl(appId: string | undefined, tenantId: string): string {
  if (!appId || !tenantId) return "";
  return `https://launcher.myapps.microsoft.com/api/signin/${encodeURIComponent(
    appId,
  )}?tenantId=${encodeURIComponent(tenantId)}`;
}

/**
 * Fetch the apps the signed-in user is assigned to and map them to
 * the portal's `AppItem` shape so they flow through the same
 * rendering + entitlement pipeline as hand-authored tiles.
 *
 * Works without throwing in CIAM tenants – the 403 on
 * `/me/appRoleAssignments` is recorded in `diagnostics.calls` so the
 * troubleshoot panel can explain the result. The returned `apps`
 * array will simply be empty in that case.
 */
export async function fetchMyAssignedApps(
  accessToken: string,
  options: {
    tenantId: string;
    excludeAppIds?: string[];
    signal?: AbortSignal;
  },
): Promise<{
  apps: AppItem[];
  warnings: string[];
  calls: DiscoveryCall[];
}> {
  const exclude = new Set(
    (options.excludeAppIds ?? []).map((s) => s.toLowerCase()),
  );
  const warnings: string[] = [];
  const calls: DiscoveryCall[] = [];

  // 1. Page through /me/appRoleAssignments
  const assignments: AppRoleAssignment[] = [];
  let nextUrl: string = `${GRAPH_BASE}/me/appRoleAssignments?$top=999`;
  let page = 0;
  while (nextUrl) {
    page += 1;
    const label = `GET /me/appRoleAssignments${page > 1 ? ` (page ${page})` : ""}`;
    const { call, json } = await timedFetch(
      nextUrl,
      accessToken,
      label,
      options.signal,
    );
    calls.push(call);
    if (!call.ok) {
      if (call.status === 403) {
        warnings.push(
          "Graph refused /me/appRoleAssignments (403). In CIAM / External ID this is expected – customer identities cannot read the directory. Populate VITE_APPS_JSON in wrangler.jsonc for these users.",
        );
      } else if (call.status === 401) {
        warnings.push(
          "Graph token rejected (401). The scopes requested (User.Read.All, Application.Read.All) may not be consented or the token has expired.",
        );
      } else if (call.errorMessage) {
        warnings.push(`/me/appRoleAssignments failed: ${call.errorMessage}`);
      }
      break;
    }
    const body = json as {
      value: AppRoleAssignment[];
      "@odata.nextLink"?: string;
    };
    assignments.push(...body.value);
    call.itemCount = body.value.length;
    nextUrl = body["@odata.nextLink"] ?? "";
  }

  if (assignments.length === 0) {
    return { apps: [], warnings, calls };
  }

  // 2. Enrich each unique resource with a service-principal lookup
  const byResource = new Map<string, AppRoleAssignment[]>();
  for (const a of assignments) {
    const bucket = byResource.get(a.resourceId) ?? [];
    bucket.push(a);
    byResource.set(a.resourceId, bucket);
  }

  const apps: AppItem[] = [];
  let enrichmentFailed = false;

  for (const [resourceId, bucket] of byResource) {
    const spUrl = `${GRAPH_BASE}/servicePrincipals/${encodeURIComponent(resourceId)}?$select=id,appId,displayName,accountEnabled,homepage,loginUrl,notes,tags,servicePrincipalType,appRoleAssignmentRequired,info`;
    const { call, json } = await timedFetch(
      spUrl,
      accessToken,
      `GET /servicePrincipals/${resourceId.slice(0, 8)}…`,
      options.signal,
    );
    calls.push(call);
    let sp: ServicePrincipal | null = null;
    if (call.ok) {
      sp = json as ServicePrincipal;
    } else if (call.status === 403 || call.status === 404) {
      enrichmentFailed = true;
    } else {
      enrichmentFailed = true;
      if (call.errorMessage) {
        warnings.push(
          `Could not load service principal ${resourceId}: ${call.errorMessage}`,
        );
      }
    }

    // MyApps "Visible to users?" = No → HideApp tag → drop.
    if (sp?.tags?.some((t) => t.toLowerCase() === "hideapp")) continue;
    if (sp && sp.accountEnabled === false) continue;
    if (sp?.appId && exclude.has(sp.appId.toLowerCase())) continue;

    const displayName =
      sp?.displayName ?? bucket[0]?.resourceDisplayName ?? "Unknown app";

    const launchUrl =
      sp?.loginUrl ||
      sp?.homepage ||
      myAppsLaunchUrl(sp?.appId, options.tenantId) ||
      "";

    apps.push({
      id: sp?.appId ?? resourceId,
      name: displayName,
      description:
        sp?.notes?.trim() ||
        `Assigned via Entra (${bucket.length} role assignment${
          bucket.length === 1 ? "" : "s"
        })`,
      icon: sp?.info?.logoUrl || "🔗",
      url: launchUrl,
      category: deriveCategory(sp),
      visible: true,
      visibleToUsers: true,
      assignmentRequired: sp?.appRoleAssignmentRequired === true,
      assignedToMe: true,
      launchType: "new_tab",
      status: launchUrl ? "available" : "restricted",
    });
  }

  if (enrichmentFailed) {
    warnings.push(
      "Some apps could not be enriched with details. Grant the SPA app registration the delegated permission Application.Read.All (admin consent required).",
    );
  }

  return {
    apps: apps.sort((a, b) => a.name.localeCompare(b.name)),
    warnings,
    calls,
  };
}
